import { Injectable } from '@angular/core';
import { catchError, from, Observable, switchMap, throwError } from 'rxjs';
import uuid from 'uuid-random';

import {
    Passkey,
    PasskeyLoginChallenge,
    PasskeyLoginResponse,
    PasskeyPutParams,
    PasskeyRegisterChallenge,
} from '../interfaces/passkeys.interface';

import { AjaxService } from 'Angular/services/core/ajax.service';
import { UrlsService } from 'Angular/services/core/urls.service';

@Injectable({
    providedIn: 'root',
})
export class PasskeysService {
    constructor(private ajaxService: AjaxService, private urlsService: UrlsService) {}

    public getPasskeys(): Observable<Passkey[]> {
        return this.ajaxService.getDataObserveBody<Passkey[]>(this.urlsService.PASSKEYS.COLLECTION);
    }

    public updatePasskey(passkeyId: number, params: PasskeyPutParams): Observable<{}> {
        const url = this.urlsService.getUrl(this.urlsService.PASSKEYS.PASSKEY, { passkeyId });
        return this.ajaxService.putDataObserveBody<{}>(url, params);
    }

    public deletePasskey(passkeyId: number): Observable<{}> {
        const url = this.urlsService.getUrl(this.urlsService.PASSKEYS.PASSKEY, { passkeyId });
        return this.ajaxService.deleteDataObserveBody<{}>(url);
    }

    public registerPasskey(): Observable<Passkey> {
        return this.getRegistrationChallenge().pipe(switchMap(this.createCredentials), switchMap(this.register));
    }

    public authenticatePasskey(): Observable<PasskeyLoginResponse> {
        return this.getLoginChallenge().pipe(switchMap(this.getCredentials), switchMap(this.loginWithSignaling));
    }

    public authenticatePasskeyConditional(): Observable<PasskeyLoginResponse> {
        return this.getLoginChallenge().pipe(
            switchMap(this.getCredentialsConditional),
            switchMap(this.loginWithSignaling)
        );
    }

    // Passkey registration helper functions

    private getRegistrationChallenge = (): Observable<PasskeyRegisterChallenge> => {
        return this.ajaxService.getDataObserveBody(this.urlsService.PASSKEYS.REGISTER);
    };

    private createCredentials = (challengeResponse: PasskeyRegisterChallenge): Observable<PublicKeyCredential> => {
        return from(
            navigator.credentials.create({
                publicKey: {
                    ...challengeResponse,
                    challenge: Uint8Array.from(challengeResponse.challenge),
                    user: {
                        ...challengeResponse.user,
                        id: Uint8Array.from(challengeResponse.user.id),
                    },
                },
            })
        ) as Observable<PublicKeyCredential>;
    };

    private register = (credentials: PublicKeyCredential): Observable<Passkey> => {
        const attestationResponse: AuthenticatorAttestationResponse =
            credentials.response as AuthenticatorAttestationResponse;
        const clientJSONBytes = new Uint8Array(attestationResponse.clientDataJSON);
        const keyBytes = new Uint8Array(attestationResponse.getPublicKey()!);
        const authBytes = new Uint8Array(attestationResponse.getAuthenticatorData());
        const registrationParams = {
            id: credentials.id,
            algorithm: attestationResponse.getPublicKeyAlgorithm(),
            publicKeyDer: btoa(String.fromCharCode(...keyBytes)),
            clientData: btoa(String.fromCharCode(...clientJSONBytes)),
            authData: btoa(String.fromCharCode(...authBytes)),
            browserUuid: uuid(),
        };
        return this.ajaxService.postJsonDataObserveBody<Passkey>(
            this.urlsService.PASSKEYS.REGISTER,
            registrationParams
        );
    };

    // Passkey authentication helper functions

    private getLoginChallenge(): Observable<PasskeyLoginChallenge> {
        return this.ajaxService.getDataObserveBody(this.urlsService.PASSKEYS.LOGIN);
    }

    private getCredentials = (challengeResponse: PasskeyLoginChallenge): Observable<PublicKeyCredential> => {
        return from(
            navigator.credentials.get({
                publicKey: {
                    challenge: Uint8Array.from(challengeResponse.challenge),
                },
            })
        ) as Observable<PublicKeyCredential>;
    };

    private getCredentialsConditional = (challengeResponse: PasskeyLoginChallenge): Observable<PublicKeyCredential> => {
        return from(
            navigator.credentials.get({
                publicKey: {
                    challenge: Uint8Array.from(challengeResponse.challenge),
                },
                mediation: 'conditional',
            })
        ) as Observable<PublicKeyCredential>;
    };

    private loginWithSignaling = (credentials: PublicKeyCredential): Observable<PasskeyLoginResponse> => {
        return this.login(credentials).pipe(
            catchError((error) => {
                if (error?.status === 403 && error?.error?.error === 'credential_unknown') {
                    this.signalUnknownCredential(credentials.id);
                }
                return throwError(() => error);
            })
        );
    };

    private signalUnknownCredential(credentialId: string): void {
        const PKC = window.PublicKeyCredential as unknown as {
            signalUnknownCredential?: (options: { rpId: string; credentialId: string }) => Promise<void>;
        };
        if (typeof PKC?.signalUnknownCredential !== 'function') {
            return;
        }
        PKC.signalUnknownCredential({ rpId: window.location.hostname, credentialId }).catch(() => {});
    }

    private login = (credentials: PublicKeyCredential): Observable<PasskeyLoginResponse> => {
        const assertionResponse: AuthenticatorAssertionResponse =
            credentials.response as AuthenticatorAssertionResponse;
        const jsonBytes = new Uint8Array(assertionResponse.clientDataJSON);
        const authBytes = new Uint8Array(assertionResponse.authenticatorData);
        const sigBytes = new Uint8Array(assertionResponse.signature);
        const userIdBytes = new Uint8Array(assertionResponse.userHandle!);
        const loginData = {
            id: credentials.id,
            clientData: btoa(String.fromCharCode(...jsonBytes)),
            authData: btoa(String.fromCharCode(...authBytes)),
            signature: btoa(String.fromCharCode(...sigBytes)),
            userId: btoa(String.fromCharCode(...userIdBytes)),
        };
        // Find where to redirect
        const searchParams = new URLSearchParams(window.location.search);
        const next = searchParams.get('next') || '/dashboard/'; // Default to dashboard, so the server doesn't try to redirect to 'null'
        const url = this.urlsService.getUrl(this.urlsService.PASSKEYS.LOGIN, {}, { next });

        return this.ajaxService.postJsonDataObserveBody<PasskeyLoginResponse>(url, loginData);
    };
}
